import { computed, reactive } from 'vue'
import { AxiosError } from 'axios'

import allValidates from '@/utils/validates'
import { useAR } from '@/store/Pinia/useAR.store'
import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
import acManagerController from '@/infra/EmailOwnership/acManager.controller'
import msValidationController from '@/infra/EmailOwnership/msValidation.controller'

type Status =
  | 'idle'
  | 'checking'
  | 'sending'
  | 'otp-pending'
  | 'validating'
  | 'verified'
  | 'blocked'
  | 'error'

type CheckContext = {
  email: string
  identity: string
  recipientFullName: string
}

const BLOCKED_MESSAGE =
  'O e-mail informado já está vinculado a outro CPF. Por favor, informe um e-mail diferente.'

const DEFAULT_ERROR_MESSAGE =
  'Não foi possível verificar a propriedade do e-mail devido a um erro de comunicação com o serviço externo.'

function extractErrorMessage(error: unknown): string {
  const err = error as AxiosError<{ message?: string }>
  return err.response?.data?.message || DEFAULT_ERROR_MESSAGE
}

// Orquestra a verificação de posse de e-mail (AC Manager + MS Validation),
// disparada explicitamente ao clicar em Avançar (sem debounce/autocheck).
// Autenticado pela sessão do Bird ID (cessToken, obtido em just-auth/login),
// enviada em toda requisição; sem recaptcha.
export function useEmailOwnershipVerification() {
  const state = reactive({
    status: 'idle' as Status,
    currentEmail: '',
    verifiedEmail: '',
    blockedEmail: '',
    otpPrefix: '',
    otpError: '',
    genericErrorMessage: '',
    lastContext: null as CheckContext | null,
  })

  // Resolve a Promise pendente de verify() quando o fluxo chega a um estado terminal
  let pendingResolve: ((verified: boolean) => void) | null = null

  function settle(verified: boolean) {
    const resolve = pendingResolve
    pendingResolve = null
    resolve?.(verified)
  }

  const isVerifying = computed(() =>
    ['checking', 'sending', 'validating'].includes(state.status),
  )
  const showBlockedModal = computed(() => state.status === 'blocked')
  const showOtpModal = computed(
    () => state.status === 'otp-pending' || state.status === 'validating',
  )
  const showGenericErrorModal = computed(() => state.status === 'error')
  const fieldError = computed(() =>
    state.blockedEmail && state.blockedEmail === state.currentEmail
      ? BLOCKED_MESSAGE
      : '',
  )

  async function sendVerification(context: CheckContext) {
    state.status = 'sending'
    try {
      const arStore = useAR()
      const solicitationStore = useSolicitation()
      const { data } =
        await msValidationController.sendEmailOwnershipVerification({
          email: context.email,
          recipientFullName: context.recipientFullName,
          logoLink: arStore.arData.logo,
          isPartner: false,
          recipientIdentity: context.identity,
          cessToken: solicitationStore.getCessToken,
        })
      state.otpPrefix = data.otp_prefix
      state.otpError = ''
      state.status = 'otp-pending'
    } catch (error) {
      state.genericErrorMessage = extractErrorMessage(error)
      state.status = 'error'
    }
  }

  async function checkOwnership(context: CheckContext) {
    state.blockedEmail = ''
    state.status = 'checking'
    try {
      const solicitationStore = useSolicitation()
      await acManagerController.checkEmailOwnership({
        email: context.email,
        identity: context.identity,
        cessToken: solicitationStore.getCessToken,
      })
      state.verifiedEmail = context.email
      state.status = 'verified'
      settle(true)
    } catch (error) {
      const err = error as AxiosError

      if (err.response?.status === 404) {
        await sendVerification(context)
        return
      }

      if (err.response?.status === 403) {
        state.blockedEmail = context.email
        state.status = 'blocked'
        return
      }

      state.genericErrorMessage = extractErrorMessage(error)
      state.status = 'error'
    }
  }

  // Dispara a verificação (chamada apenas ao clicar em Avançar) e resolve
  // quando o e-mail é confirmado (true) ou o usuário precisa corrigi-lo (false).
  function verify(context: CheckContext): Promise<boolean> {
    state.currentEmail = context.email

    if (!context.email || !allValidates.validateEmail(context.email)) {
      return Promise.resolve(true)
    }

    if (context.email === state.verifiedEmail) {
      return Promise.resolve(true)
    }

    state.lastContext = context

    return new Promise<boolean>((resolve) => {
      pendingResolve = resolve
      checkOwnership(context)
    })
  }

  function updateCurrentEmail(email: string) {
    state.currentEmail = email
  }

  async function submitOtp(otp: string) {
    if (!state.lastContext) {
      return
    }

    state.status = 'validating'
    state.otpError = ''
    try {
      const solicitationStore = useSolicitation()
      // MS Validation espera o código concatenado com o prefixo: PREFIX-OTP_CODE
      await msValidationController.validateEmailOwnershipOtp({
        otp: `${state.otpPrefix}-${otp}`,
        cessToken: solicitationStore.getCessToken,
      })
      state.verifiedEmail = state.lastContext.email
      state.status = 'verified'
      settle(true)
    } catch (_error) {
      state.otpError = 'OTP inválido'
      state.status = 'otp-pending'
    }
  }

  async function resendCode() {
    if (!state.lastContext) {
      return
    }
    await sendVerification(state.lastContext)
  }

  function closeBlockedModal() {
    state.status = 'idle'
    settle(false)
  }

  function closeOtpModal() {
    state.status = 'idle'
    state.otpPrefix = ''
    state.otpError = ''
    settle(false)
  }

  function closeGenericErrorModal() {
    state.status = 'idle'
    settle(false)
  }

  return reactive({
    otpPrefix: computed(() => state.otpPrefix),
    otpError: computed(() => state.otpError),
    genericErrorMessage: computed(() => state.genericErrorMessage),
    isVerifying,
    showBlockedModal,
    showOtpModal,
    showGenericErrorModal,
    fieldError,
    verify,
    updateCurrentEmail,
    submitOtp,
    resendCode,
    closeBlockedModal,
    closeOtpModal,
    closeGenericErrorModal,
  })
}
